import { useEffect, useRef } from "react";
import intl from "src/internationalization/intl";
import {
    ObjectiveChangedAction,
    ObjectiveStreamEvent,
    subscribeObjectiveEventsStream,
} from "src/services/api/opportunities/events";
import { useOpportunitiesStore } from "src/store";
import { useSnackbarStore } from "src/store/snackbarStore";
import { useUserStore } from "src/store/userStore";

const INITIAL_RECONNECT_MS = 2_000;
const MAX_RECONNECT_MS = 30_000;

function notificationKeyForAction(action: ObjectiveChangedAction): string {
    switch (action) {
        case "created":
            return "opportunityCreatedNotification";
        case "updated":
            return "opportunityUpdatedNotification";
        case "deleted":
            return "opportunityDeletedNotification";
    }
}

/**
 * Keeps an SSE connection open while the opportunities section is mounted.
 * Refreshes the list and shows a snackbar when another user changes objectives.
 */
export function useObjectiveEventsChannel(): void {
    const backendToken = useUserStore((s) => s.tokens.backendToken);
    const currentUserId = useUserStore((s) => s.user?.user_id);
    const triggerRefresh = useOpportunitiesStore((s) => s.triggerRefresh);
    const showSnackbar = useSnackbarStore((s) => s.showSnackbar);
    const reconnectDelayRef = useRef(INITIAL_RECONNECT_MS);

    useEffect(() => {
        if (!backendToken) return;

        const abort = new AbortController();
        let reconnectTimer: number | undefined;
        let disposed = false;

        const scheduleReconnect = () => {
            if (disposed || abort.signal.aborted) return;
            reconnectTimer = window.setTimeout(() => {
                void connect();
            }, reconnectDelayRef.current);
            reconnectDelayRef.current = Math.min(
                reconnectDelayRef.current * 2,
                MAX_RECONNECT_MS,
            );
        };

        const handleEvent = (event: ObjectiveStreamEvent) => {
            if (event.type === "connected") {
                reconnectDelayRef.current = INITIAL_RECONNECT_MS;
                return;
            }
            if (event.type === "objective_movements_ready") {
                triggerRefresh();
                return;
            }
            if (event.type !== "objective_changed") return;

            triggerRefresh();

            const isOwnAction =
                event.actor_user_id &&
                currentUserId &&
                event.actor_user_id === currentUserId;
            if (isOwnAction) return;

            const title = event.title?.trim() || intl("opportunityUntitled");
            const message = intl(notificationKeyForAction(event.action), {
                values: { title },
            });
            showSnackbar(message, "info");
        };

        const connect = async () => {
            try {
                await subscribeObjectiveEventsStream({
                    signal: abort.signal,
                    onEvent: handleEvent,
                });
            } catch {
                if (!disposed && !abort.signal.aborted) {
                    scheduleReconnect();
                }
                return;
            }

            if (!disposed && !abort.signal.aborted) {
                scheduleReconnect();
            }
        };

        void connect();

        return () => {
            disposed = true;
            abort.abort();
            if (reconnectTimer !== undefined) {
                window.clearTimeout(reconnectTimer);
            }
        };
    }, [backendToken, currentUserId, showSnackbar, triggerRefresh]);
}
